import React from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useNewsfeed from "../../zustand/UseNewsfeed";
import { X, Heart, Award, Star, Flag, ArrowLeft } from "lucide-react";
import useUserStore from "../../zustand/UseUserStore";
import { newsfeedApi } from "../../api/newsFeedApi";
import { IMAGE_PATH } from "../../api/apiConfig";
import { userAuthAPI } from "../../api/userAuthAPI";
import Camera from "./Camera";
import CaptureMethodSelector from "./CaptureMethodSelector";
import Waveform from "./Waveform";
import PostTypeSelector from "./PostTypeSelector";
import ContestField from "./ContestField";
import PostFormInputs from "./PostFormInputs";
import MediaCaptureSection from "./MediaCaptureSection";

const ComposePost = ({
	onContestChange,
	selectedPostType,
	onPostTypeSelect,
	borderColor = "border-yellow-500",
	currentContestId = 0,
	onPostCreated,
	onClose,
}) => {
	const [mediaPreview, setMediaPreview] = useState(null);
	const [mediaFile, setMediaFile] = useState(null);
	const [mediaType, setMediaType] = useState(null);
	const [error, setError] = useState(null);
	const [title, setTitle] = useState("");
	const [content, setContent] = useState("");
	const [isPosting, setIsPosting] = useState(false);
	const [activeEvent, setActiveEvent] = useState(null);
	const [proudOptions, setProudOptions] = useState([]);
	const [selectedProudOption, setSelectedProudOption] = useState(null);
	const [showPostPreview, setShowPostPreview] = useState(false);
	const [isLoadingProudOptions, setIsLoadingProudOptions] = useState(false);
	const [isListening, setIsListening] = useState(false);
	const [showCaptureSelector, setShowCaptureSelector] = useState(false);
	const [showCamera, setShowCamera] = useState(false);
	const [cameraMode, setCameraMode] = useState("photo"); // 'photo' or 'video'
	const [autoTriggerUpload, setAutoTriggerUpload] = useState(false);

	const fileInputRef = useRef(null);
	const audioInputRef = useRef(null);
	const videoInputRef = useRef(null);
	const audioContextRef = useRef(null);
	const animationFrameRef = useRef(null);
	const videoRef = useRef(null);
	const audioElementRef = useRef(null);
	const playbackAnalyserRef = useRef(null);
	const playbackSourceRef = useRef(null); // Store media element source node
	const staticWaveformRef = useRef(null); // Store static waveform for restoration
	const isAudioPlayingRef = useRef(false); // Track playing state for visualization closure
	const [audioLevels, setAudioLevels] = useState([]);
	const [isAudioPlaying, setIsAudioPlaying] = useState(false);
	const [audioDuration, setAudioDuration] = useState(0);
	const [audioCurrentTime, setAudioCurrentTime] = useState(0);
	const [isAudioLoading, setIsAudioLoading] = useState(false);
	const textareaRef = useRef(null);
	const postButtonRef = useRef(null);

	// Scroll the post button into view after the user finishes typing the caption
	const handleContentBlur = () => {
		if (!content || !content.trim()) {return;}
		// Defer to next frame so layout settles (esp. when mobile keyboard dismisses)
		requestAnimationFrame(() => {
			postButtonRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "end",
			});
		});
	};

	// Note: Time updates are handled by onTimeUpdate event on the audio element
	// This is more reliable than requestAnimationFrame, especially on Safari
	const recognitionRef = useRef(null);
	const recognitionBaseContentRef = useRef(""); // Track content when recognition started
	const recognitionFinalTextRef = useRef(""); // Track what's been finalized so far

	// Zustand stores
	const newsfeed = useNewsfeed();
	const userStore = useUserStore();
	const navigate = useNavigate();
	const contests = [
		"Glow Game",
		...userStore
			.getContestNames()
			.filter((name) => name.toLowerCase() !== "the glow game"),
	];

	// Helper function to convert contest ID to index
	const getContestIndexFromID = (contestID) => {
		if (contestID === 0) {
			return 0;
		} // "Glow Game"

		// Find the contest role
		const contestRole = userStore.user_roles.find(
			(role) => role.ContestID === contestID
		);

		if (contestRole) {
			// Find the index in contests array
			const index = contests.findIndex(
				(c) => c.toLowerCase() === contestRole.ContestName.toLowerCase()
			);
			return index > 0 ? index : 0;
		}

		return 0;
	};

	// Calculate initial contest index from currentContestId prop
	// This is computed synchronously so there's no flash
	const initialContestIndex = useRef(getContestIndexFromID(currentContestId));
	const [contestIndex, setContestIndex] = useState(initialContestIndex.current);

	// Sync contestIndex with currentContestId prop (from Newsfeed filters)
	// currentContestId is a contest ID, not an index
	// This runs whenever currentContestId changes
	useEffect(() => {
		const indexFromID = getContestIndexFromID(currentContestId);
		if (indexFromID !== contestIndex) {
			console.log(
				"🔄 Syncing ComposePost contest - ID:",
				currentContestId,
				"Index:",
				indexFromID
			);
			setContestIndex(indexFromID);
		}
	}, [currentContestId, contests.length]); // Re-run if contests array changes too

	// Set initial contest to PrevContest on component mount
	// BUT only if currentContestId matches PrevContest (respect user's current selection)
	useEffect(() => {
		const prevContestID = userStore.user?.PrevContest;

		// Only set if:
		// 1. We have a PrevContest (including 0 for Glow Game)
		// 2. The currentContestId prop matches PrevContest (user's selection matches saved preference)
		// 3. The contestIndex doesn't match the PrevContest yet
		// This respects the user's saved preference while not overriding explicit selections
		if (
			prevContestID !== undefined &&
			prevContestID !== null &&
			currentContestId === prevContestID
		) {
			if (prevContestID === 0) {
				// Glow Game - ensure contestIndex is 0
				if (contestIndex !== 0) {
					console.log("Setting contest to Glow Game from PrevContest");
					setContestIndex(0);
					setActiveEvent(null);
				}
			} else {
				// Find the contest name from user roles
				const prevContestRole = userStore.user_roles.find(
					(role) => role.ContestID === prevContestID
				);

				if (prevContestRole) {
					// Find the index of this contest in the contests array
					const contestName = prevContestRole.ContestName;
					const index = contests.findIndex(
						(c) => c.toLowerCase() === contestName.toLowerCase()
					);

					if (index > 0 && index !== contestIndex) {
						// Skip index 0 which is "Glow Game"
						console.log(
							"Setting initial contest from PrevContest:",
							contestName,
							"at index:",
							index
						);
						setContestIndex(index);

						// Fetch active event for this contest
						newsfeedApi
							.getActiveEventForContest(prevContestID)
							.then((response) => {
								if (response.data?.result?.activeEvent) {
									setActiveEvent(response.data.result.activeEvent);
								}
							})
							.catch((error) => {
								console.error("Error fetching active event:", error);
							});
					}
				}
			}
		}
	}, [userStore.user_roles.length, currentContestId]); // Run when user roles are loaded or currentContestId changes

	// Handle contest change and notify parent component
	async function handleContestChange(index) {
		let newIndex;
		if (index < 0) {
			newIndex = contests.length - 1;
		} else if (index >= contests.length) {
			newIndex = 0;
		} else {
			newIndex = index;
		}
		setContestIndex(newIndex);

		// Auto-switch to "glow" post type when selecting a contest (not "Glow Game" or "The Glow Game")
		const contestName = contests[newIndex]?.toLowerCase() || "";
		if (contestName !== "glow game" && contestName !== "the glow game") {
			if (onPostTypeSelect) {
				onPostTypeSelect("glow");
			}
		} else {
			// Reset post type when switching to Glow Game to show all options
			if (onPostTypeSelect) {
				onPostTypeSelect(null);
			}
		}

		// Fetch active event for the selected contest and update PrevContest
		let contestid = null;
		if (contestName !== "glow game" && contestName !== "the glow game") {
			userStore.user_roles.forEach((role) => {
				if (role.ContestName === contests[newIndex]) {
					contestid = role.ContestID;
				}
			});

			if (contestid) {
				try {
					// Update member's PrevContest in the database
					await userAuthAPI.updateMember({
						MemberID: userStore.user?.MemberID,
						PrevContest: contestid,
					});
					console.log("✅ Updated PrevContest in database:", contestid);

					// Update Zustand store to keep state in sync
					const currentUser = useUserStore.getState().user;
					userStore.setUser({
						...currentUser,
						PrevContest: contestid,
					});
					console.log("✅ Updated PrevContest in Zustand store:", contestid);
				} catch (error) {
					console.error("Failed to update PrevContest:", error);
				}

				try {
					const response =
						await newsfeedApi.getActiveEventForContest(contestid);
					if (response.data?.result?.activeEvent) {
						setActiveEvent(response.data.result.activeEvent);
					} else {
						setActiveEvent(null);
					}
				} catch (error) {
					console.error("Error fetching active event:", error);
					setActiveEvent(null);
				}
			}
		} else {
			setActiveEvent(null);
			contestid = 0; // Glow Game

			// Save Glow Game (contestID 0) as PrevContest
			try {
				await userAuthAPI.updateMember({
					MemberID: userStore.user?.MemberID,
					PrevContest: 0,
				});
				console.log("✅ Updated PrevContest to Glow Game (0) in database");

				// Update Zustand store to keep state in sync
				const currentUser = useUserStore.getState().user;
				userStore.setUser({
					...currentUser,
					PrevContest: 0,
				});
				console.log("✅ Updated PrevContest to Glow Game (0) in Zustand store");
			} catch (error) {
				console.error("Failed to update PrevContest to Glow Game:", error);
			}
		}

		// Notify parent component of contest change with contest ID (not index!)
		if (onContestChange) {
			onContestChange(contestid);
		}
	}

	// Speech recognition functions
	const startSpeechRecognition = () => {
		// Check if browser supports speech recognition
		const SpeechRecognition =
			window.SpeechRecognition || window.webkitSpeechRecognition;
		if (!SpeechRecognition) {
			setError("Speech recognition is not supported in your browser.");
			return;
		}

		// Initialize recognition if not already done
		if (!recognitionRef.current) {
			recognitionRef.current = new SpeechRecognition();
			recognitionRef.current.continuous = true;
			recognitionRef.current.interimResults = true;
			recognitionRef.current.lang = "en-US";

			recognitionRef.current.onresult = (event) => {
				let interimTranscript = "";
				let newFinalTranscript = "";

				// Process all results from the last resultIndex
				for (let i = event.resultIndex; i < event.results.length; i++) {
					const { transcript } = event.results[i][0];
					if (event.results[i].isFinal) {
						// Only add new final text that hasn't been added yet
						newFinalTranscript += `${transcript} `;
					} else {
						// Track interim results separately
						interimTranscript += transcript;
					}
				}

				// Update the final text tracker
				if (newFinalTranscript) {
					recognitionFinalTextRef.current += newFinalTranscript;
				}

				// Update content: base + all finalized text + current interim
				setContent(() => {
					return (
						recognitionBaseContentRef.current +
						recognitionFinalTextRef.current +
						(interimTranscript ? ` ${interimTranscript}` : "")
					);
				});
			};

			recognitionRef.current.onerror = (event) => {
				console.error("Speech recognition error:", event.error);
				setIsListening(false);
				if (event.error === "not-allowed") {
					setError(
						"Microphone permission denied. Please allow microphone access."
					);
				} else if (event.error === "no-speech") {
					// This is fine, user just didn't speak
				} else {
					setError("Speech recognition error. Please try again.");
				}
			};

			recognitionRef.current.onend = () => {
				setIsListening(false);
				// Finalize any remaining content - preserve all recognized text
				setContent((currentContent) => {
					const baseContent = recognitionBaseContentRef.current || "";
					const finalText = recognitionFinalTextRef.current || "";
					const combined = (baseContent + finalText).trim();
					// Preserve the recognized text - only update if we have something
					if (combined) {
						return combined;
					}
					// If no recognized text, keep current content
					return currentContent;
				});
				// Don't reset refs yet - let them be cleared when new recognition starts
			};
		}

		// Store the current content as the baseline when recognition starts
		// If user has already typed something or there's existing content, preserve it
		recognitionBaseContentRef.current = content || "";
		recognitionFinalTextRef.current = "";

		try {
			recognitionRef.current.start();
			setIsListening(true);
			setError(null);
		} catch (error) {
			console.error("Error starting speech recognition:", error);
			setError("Failed to start speech recognition. Please try again.");
			setIsListening(false);
		}
	};

	const stopSpeechRecognition = () => {
		if (recognitionRef.current && isListening) {
			try {
				recognitionRef.current.stop();
			} catch (error) {
				console.error("Error stopping speech recognition:", error);
			}
		}
		setIsListening(false);
		// Finalize content immediately - preserve all recognized text
		setContent((currentContent) => {
			const baseContent = recognitionBaseContentRef.current || "";
			const finalText = recognitionFinalTextRef.current || "";
			const combined = (baseContent + finalText).trim();
			// Preserve the recognized text
			if (combined) {
				return combined;
			}
			// If no recognized text, keep current content
			return currentContent;
		});
		// Don't reset refs - the text is now in state, refs will be reset on next start
	};

	// Set up audio visualization for playback - Safari-safe rebuild logic
	useEffect(() => {
		if (!mediaPreview || mediaType !== "audio" || !audioElementRef.current) {
			return;
		}

		const audioElement = audioElementRef.current;

		// Create AudioContext if missing
		if (!audioContextRef.current) {
			audioContextRef.current = new (
				window.AudioContext || window.webkitAudioContext
			)();
			console.log("ComposePost: AudioContext created");
		}

		const audioCtx = audioContextRef.current;

		// SAFARI: Detect if audio element has changed since last render
		const sourceIsStale =
			playbackSourceRef.current &&
			playbackSourceRef.current.mediaElement !== audioElement;

		if (sourceIsStale) {
			console.warn(
				"ComposePost: Audio element changed → rebuilding WebAudio graph"
			);

			try {
				playbackSourceRef.current.disconnect();
			} catch {}
			try {
				playbackAnalyserRef.current?.disconnect();
			} catch {}

			playbackSourceRef.current = null;
			playbackAnalyserRef.current = null;
		}

		// Create new graph if missing
		if (!playbackSourceRef.current) {
			try {
				const analyser = audioCtx.createAnalyser();
				analyser.fftSize = 2048;
				analyser.smoothingTimeConstant = 0.3; // More responsive, matches Camera.jsx

				const source = audioCtx.createMediaElementSource(audioElement);

				source.connect(analyser);
				analyser.connect(audioCtx.destination);

				playbackSourceRef.current = source;
				playbackAnalyserRef.current = analyser;

				console.log(
					"ComposePost: New WebAudio graph created for audio element"
				);
			} catch (err) {
				console.error("ComposePost: WebAudio graph creation failed:", err);
			}
		}

		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = null;
			}
		};
	}, [mediaPreview, mediaType]);

	// Cleanup speech recognition on unmount
	useEffect(() => {
		return () => {
			if (recognitionRef.current) {
				try {
					recognitionRef.current.stop();
				} catch (error) {
					// Ignore errors on cleanup
				}
				recognitionRef.current = null;
			}
		};
	}, []);

	// Auto-trigger camera for glow/grateful posts immediately when modal opens
	// BUT NOT for contest submissions (user must click "Submit for Contest" button)
	useEffect(() => {
		const isContest =
			contests[contestIndex]?.toLowerCase() !== "glow game" &&
			contests[contestIndex]?.toLowerCase() !== "the glow game";

		const needsMedia =
			selectedPostType !== "pride" &&
			!mediaFile &&
			!mediaPreview &&
			!showCamera &&
			!showCaptureSelector &&
			!isContest; // Don't auto-trigger for contests

		if (needsMedia && selectedPostType) {
			// Open capture method selector
			setShowCaptureSelector(true);
		}
	}, [selectedPostType]); // Only run when post type changes

	// Handle capture method selection
	const handleCaptureMethodSelect = (method) => {
		setShowCaptureSelector(false);

		// Open camera with selected mode (or "photo" mode for upload)
		setCameraMode(method === "upload" ? "photo" : method);
		setAutoTriggerUpload(method === "upload");
		setShowCamera(true);
	};

	// Closes any active media
	function handleRemoveMedia() {
		// Revoke object URL for media preview
		if (mediaPreview && typeof mediaPreview === "string") {
			URL.revokeObjectURL(mediaPreview);
		}

		// Stop audio if playing
		if (audioElementRef.current) {
			audioElementRef.current.pause();
			audioElementRef.current.currentTime = 0;
		}

		// Clean up audio visualization
		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
			animationFrameRef.current = null;
		}

		// Reset analyser refs so they can be recreated
		playbackAnalyserRef.current = null;
		playbackSourceRef.current = null;

		// Reset media state
		setMediaPreview(null);
		setMediaFile(null);
		setMediaType(null);
		setError(null);

		// Reset audio player state
		isAudioPlayingRef.current = false;
		setIsAudioPlaying(false);
		setAudioDuration(0);
		setAudioCurrentTime(0);
		setAudioLevels([]);

		// Reset file inputs
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
		if (audioInputRef.current) {
			audioInputRef.current.value = "";
		}
		if (videoInputRef.current) {
			videoInputRef.current.value = "";
		}
	}

	// Handles file selection
	function handleFileSelect(e, type) {
		const file = e.target.files[0];
		if (!file) {
			return;
		}

		// Clear previous error
		setError(null);

		// Create preview URL
		const previewUrl = URL.createObjectURL(file);
		setMediaPreview(previewUrl);
		setMediaFile(file);
		setMediaType(type);
	}

	// Handle camera capture
	const handleCameraCapture = async (file) => {
		// Clear previous error
		setError(null);

		// Determine file type
		let type = "image";
		if (file.type.startsWith("video/")) {
			type = "video";
		} else if (file.type.startsWith("audio/")) {
			type = "audio";
		}

		// Create preview URL
		const previewUrl = URL.createObjectURL(file);

		// If it's audio, extract the static waveform
		if (type === "audio") {
			setIsAudioLoading(true);
			try {
				const tempAudioContext = new (
					window.AudioContext || window.webkitAudioContext
				)();
				const arrayBuffer = await file.arrayBuffer();
				const audioBuffer = await tempAudioContext.decodeAudioData(arrayBuffer);

				// CRITICAL: Set duration from decoded audio buffer
				// WebM blobs often report Infinity for duration in HTML5 audio element
				if (
					audioBuffer.duration &&
					isFinite(audioBuffer.duration) &&
					!isNaN(audioBuffer.duration) &&
					audioBuffer.duration > 0
				) {
					setAudioDuration(audioBuffer.duration);
					console.log(
						"ComposePost: Duration set from audioBuffer (camera capture):",
						audioBuffer.duration
					);
				}

				// Extract waveform similar to AudioPlayer
				const samples = 1024;
				const channelData = audioBuffer.getChannelData(0);
				if (channelData && channelData.length > 0) {
					const blockSize = Math.floor(channelData.length / samples);
					const waveform = [];
					let globalMax = 0;
					const rmsValues = [];

					for (let i = 0; i < samples; i++) {
						const startIdx = i * blockSize;
						const endIdx = Math.min(startIdx + blockSize, channelData.length);

						// Calculate RMS for this block
						let sumSquares = 0;
						let count = 0;
						for (let j = startIdx; j < endIdx; j++) {
							sumSquares += channelData[j] * channelData[j];
							count++;
						}
						const rms = count > 0 ? Math.sqrt(sumSquares / count) : 0;
						rmsValues.push(rms);
						globalMax = Math.max(globalMax, rms);
					}

					// Normalize using global max to preserve relative differences
					const normalizeFactor = globalMax > 0 ? 127 / globalMax : 1;
					for (let i = 0; i < rmsValues.length; i++) {
						const normalized = Math.max(
							0,
							Math.min(255, 128 + rmsValues[i] * normalizeFactor)
						);
						waveform.push(normalized);
					}

					// Store static waveform in ref for restoration when paused
					staticWaveformRef.current = waveform;
					setAudioLevels(waveform);
					console.log(
						"ComposePost: Static waveform extracted from camera capture, length:",
						waveform.length
					);
				} else {
					// Default waveform if extraction fails
					const defaultWaveform = new Array(1024).fill(128);
					staticWaveformRef.current = defaultWaveform;
					setAudioLevels(defaultWaveform);
				}
				tempAudioContext.close();
			} catch (waveformError) {
				console.error(
					"ComposePost: Error extracting waveform from camera capture:",
					waveformError
				);
				// Default waveform on error
				const defaultWaveform = new Array(1024).fill(128);
				staticWaveformRef.current = defaultWaveform;
				setAudioLevels(defaultWaveform);
			} finally {
				// Always clear loading state
				setIsAudioLoading(false);
			}
		} else {
			// Not audio, clear loading state immediately
			setIsAudioLoading(false);
		}

		setMediaPreview(previewUrl);
		setMediaFile(file);
		setMediaType(type);
	};

	// Audio/video recording is handled entirely by the Camera component.
	// Dead recording functions (startAudioRecording, stopRecording,
	// startVideoRecording, stopAllRecordings) were removed in this refactor.
	//
	// The recording timer useEffect was also removed since isRecording
	// and isVideoRecording are no longer tracked here.

	// Sets video srcObject when mediaPreview changes
	useEffect(() => {
		// Check if video element exists and mediaPreview is set
		if (videoRef.current && mediaPreview) {
			if (mediaType === "video-recording") {
				// For live recording, use srcObject
				videoRef.current.srcObject = mediaPreview;
				// Ensure the video plays
				videoRef.current.play().catch(console.error);
			} else if (mediaType === "video") {
				// For recorded video, use src
				videoRef.current.src = mediaPreview;
				videoRef.current.srcObject = null; // Clear srcObject
			}
		}
	}, [mediaPreview, mediaType]);

	// Auto-switch to "Glow Game" when grateful or proud post types are selected
	useEffect(() => {
		if (selectedPostType === "grateful" || selectedPostType === "pride") {
			const glowGameIndex = contests.findIndex(
				(contest) => contest.toLowerCase() === "glow game"
			);
			if (glowGameIndex !== -1 && glowGameIndex !== contestIndex) {
				handleContestChange(glowGameIndex);
			}
		}
	}, [selectedPostType, contests, contestIndex]);

	// Fetch active event when component loads
	useEffect(() => {
		const fetchInitialActiveEvent = async () => {
			const contestName = contests[contestIndex]?.toLowerCase() || "";
			if (contestName !== "glow game" && contestName !== "the glow game") {
				let contestid = null;
				userStore.user_roles.forEach((role) => {
					if (role.ContestName === contests[contestIndex]) {
						contestid = role.ContestID;
					}
				});

				if (contestid) {
					try {
						const response =
							await newsfeedApi.getActiveEventForContest(contestid);
						if (response.data?.result?.activeEvent) {
							setActiveEvent(response.data.result.activeEvent);
						} else {
							setActiveEvent(null);
						}
					} catch (error) {
						console.error("Error fetching active event:", error);
						setActiveEvent(null);
					}
				}
			}
		};

		fetchInitialActiveEvent();
	}, []); // Only run on mount

	// Fetch proud post options when pride post type is selected
	useEffect(() => {
		const fetchProudOptions = async () => {
			if (selectedPostType === "pride" && userStore.user?.MemberID) {
				setIsLoadingProudOptions(true);
				setError(null);
				try {
					const response = await newsfeedApi.getProudPostOptions(
						userStore.user.MemberID
					);
					console.log("Proud post options response:", response);

					// Transform the API response into selectable achievement objects
					const options = [];
					const result = response.data?.result;

					if (result?.achievements && result?.details) {
						const { achievements, details, canPost, alreadyPosted } = result;
						console.log("Achievements:", achievements);
						console.log("Details:", details);
						console.log("CanPost:", canPost);
						console.log("AlreadyPosted:", alreadyPosted);
						console.log("All achievement keys:", Object.keys(achievements));
						console.log("All details keys:", Object.keys(details));

						// Always show Secret Salutes with progress
						if (details.secretSalutes) {
							const isComplete =
								achievements.completedAllSecretSalutes &&
								details.secretSalutes?.allDecksComplete;
							const canPostThis = canPost?.completedAllSecretSalutes ?? false;
							const alreadyPostedThis =
								alreadyPosted?.completedAllSecretSalutes ?? false;

							options.push({
								type: "secretSalutes",
								TotalDecks: details.secretSalutes.totalDecks,
								LumensEarned: details.secretSalutes.lumensEarned,
								Completed: details.secretSalutes.completed || 0,
								Total: details.secretSalutes.total || 0,
								IsComplete: isComplete,
								CanPost: canPostThis,
								AlreadyPosted: alreadyPostedThis,
							});
						}

						// Always show Star Salutes with progress
						if (details.starSalutes) {
							const starSaluteAchievementKeys = Object.keys(
								achievements
							).filter(
								(key) =>
									key.toLowerCase().includes("star") ||
									key.toLowerCase().includes("salute")
							);

							const hasSentFiveStarSalutes =
								achievements.sentFiveStarSalutes ||
								achievements.starSalutes ||
								achievements.sentStarSalutes ||
								achievements.fiveStarSalutes ||
								starSaluteAchievementKeys.some((key) => achievements[key]);

							const isComplete =
								hasSentFiveStarSalutes && details.starSalutes.sent >= 3;
							const canPostThis = canPost?.sentThreeStarSalutes ?? false;
							const alreadyPostedThis =
								alreadyPosted?.sentThreeStarSalutes ?? false;

							options.push({
								type: "star",
								SalutesSent: details.starSalutes.sent,
								Required: details.starSalutes.required,
								IsComplete: isComplete,
								CanPost: canPostThis,
								AlreadyPosted: alreadyPostedThis,
							});
						}

						// Always show Quests with progress
						if (details.quests) {
							const questDetails = details.quests.completedQuestDetails || [];
							const hasCompletedQuest =
								achievements.completedQuest && details.quests?.completedToday;

							if (hasCompletedQuest && questDetails.length > 0) {
								// If completed, show each completed quest separately
								const canPostThis = canPost?.completedQuest ?? false;
								const alreadyPostedThis =
									alreadyPosted?.completedQuest ?? false;

								questDetails.forEach((quest) => {
									options.push({
										type: "quest",
										QuestID: quest.questID,
										QuestName: quest.questName,
										MilestonesCompleted: quest.rewardedMilestones,
										TotalMilestones:
											quest.totalMilestones || quest.rewardedMilestones,
										CompletedDate: quest.lastMilestoneUpdated,
										IsComplete: true,
										CanPost: canPostThis,
										AlreadyPosted: alreadyPostedThis,
									});
								});
							} else {
								// Show progress for incomplete quests using quest milestones
								// Check for quest1Milestones, quest2Milestones, quest3Milestones, etc.
								const questMilestones =
									details.quests.quest1Milestones ||
									details.quests.quest2Milestones ||
									details.quests.quest3Milestones ||
									details.quests.quest4Milestones ||
									details.quests.quest5Milestones;

								if (questMilestones) {
									options.push({
										type: "quest",
										QuestName: "Daily Quest",
										MilestonesCompleted: questMilestones.completed || 0,
										TotalMilestones: questMilestones.total || 1,
										IsComplete: false,
									});
								} else {
									// Fallback if no quest milestones available
									options.push({
										type: "quest",
										QuestName: "Daily Quest",
										MilestonesCompleted: 0,
										TotalMilestones: 1,
										IsComplete: false,
									});
								}
							}
						}

						// Only add completed achievements below
						if (achievements.earnedCreature && details.creatures?.earnedToday) {
							const creaturesEarned = details.creatures.creaturesEarned || [];
							const canPostThis = canPost?.earnedCreature ?? false;
							const alreadyPostedThis = alreadyPosted?.earnedCreature ?? false;

							creaturesEarned.forEach((creature) => {
								options.push({
									type: "creature",
									CreatureNumber: creature.creatureNumber,
									CreatureName: creature.creatureName,
									ImageURL: creature.imageURL,
									IsComplete: true,
									CanPost: canPostThis,
									AlreadyPosted: alreadyPostedThis,
								});
							});
						}

						if (achievements.wonContest && details.contests?.wonToday) {
							const contestsWon = details.contests.contestsWon || [];
							contestsWon.forEach((contest) => {
								options.push({
									type: "contestWin",
									ContestName: contest.contestName || "Contest",
									ContestID: contest.contestID,
									IsComplete: true,
								});
							});
						}

						if (
							achievements.createdFirstGlow &&
							details.firstGlow?.createdToday
						) {
							const canPostThis = canPost?.createdFirstGlow ?? false;
							const alreadyPostedThis =
								alreadyPosted?.createdFirstGlow ?? false;

							options.push({
								type: "firstGlow",
								GlowID: details.firstGlow.glowDetails?.glowID,
								Title: details.firstGlow.glowDetails?.title,
								CreatedAt: details.firstGlow.glowDetails?.createdAt,
								LumensEarned: details.firstGlow.glowDetails?.lumens || 0,
								IsComplete: true,
								CanPost: canPostThis,
								AlreadyPosted: alreadyPostedThis,
							});
						}
					}

					console.log("Final options array:", options);
					setProudOptions(options);
				} catch (error) {
					console.error("Error fetching proud post options:", error);
					setError("Failed to load achievements. Please try again.");
					setProudOptions([]);
				} finally {
					setIsLoadingProudOptions(false);
				}
			} else {
				setProudOptions([]);
				setSelectedProudOption(null);
			}
		};

		fetchProudOptions();
	}, [selectedPostType, userStore.user?.MemberID]);

	// Handles preview button for proud posts
	const handleShowPreview = () => {
		if (selectedPostType === "pride" && selectedProudOption) {
			// Auto-populate body from selected achievement, but leave title empty
			const achievementBody = getAchievementBody(selectedProudOption);
			setTitle(""); // Keep title empty
			setContent(achievementBody);
			setShowPostPreview(true);
		}
	};

	// Handles post submission
	const handleSubmit = async () => {
		try {
			// Check if user has enough lumens for grateful posts
			if (selectedPostType === "grateful") {
				const currentLumens = userStore.user?.CurrentLumens || 0;
				if (currentLumens < 2) {
					setError("You need at least 2 lumens to post a grateful post.");
					return;
				}
			}

			// Check if this pride post can be posted
			if (selectedPostType === "pride" && selectedProudOption) {
				if (selectedProudOption.CanPost === false) {
					setError(
						selectedProudOption.AlreadyPosted
							? "You have already posted this achievement today."
							: "This achievement cannot be posted at this time."
					);
					return;
				}
			}

			setIsPosting(true);

			let contestid = null;
			userStore.user_roles.forEach((role) => {
				if (role.ContestName === contests[contestIndex]) {
					contestid = role.ContestID;
				}
			});

			// Submit post to newsfeed with post type
			console.log("📤 Submitting post with type:", selectedPostType);

			// For creature pride posts, fetch the creature image as a file
			let finalMediaFile = mediaFile;
			let finalMediaType = mediaType;

			if (
				selectedPostType === "pride" &&
				selectedProudOption?.type === "creature"
			) {
				try {
					// Map creature number to image path
					const creatureMap = {
						1: `${IMAGE_PATH}/quest/creatures/Jokeymon.png`,
						2: `${IMAGE_PATH}/quest/creatures/Patience.png`,
						3: `${IMAGE_PATH}/quest/creatures/Stevie.png`,
						4: `${IMAGE_PATH}/quest/creatures/bluekin.png`,
						5: `${IMAGE_PATH}/quest/creatures/lightkin.png`,
					};
					const creatureImagePath =
						creatureMap[selectedProudOption.CreatureNumber];
					console.log("🎨 Fetching creature image:", creatureImagePath);

					// Fetch the creature image and convert to blob
					const response = await fetch(creatureImagePath);
					const blob = await response.blob();

					// Create a File object from the blob
					const fileName = creatureImagePath.split("/").pop();
					finalMediaFile = new File([blob], fileName, { type: blob.type });
					finalMediaType = "image";

					console.log("✅ Creature image loaded as file:", {
						name: fileName,
						size: finalMediaFile.size,
						type: finalMediaFile.type,
					});
				} catch (error) {
					console.error("❌ Error loading creature image:", error);
					// Continue with post submission without the image
				}
			} else if (
				selectedPostType === "pride" &&
				selectedProudOption?.type === "star"
			) {
				try {
					// Use the stars image for star salute posts
					const starsImagePath = `${IMAGE_PATH}/newsfeed/stars.jpg`;
					console.log("⭐ Fetching stars image:", starsImagePath);

					// Fetch the stars image and convert to blob
					const response = await fetch(starsImagePath);
					const blob = await response.blob();

					// Create a File object from the blob
					const fileName = "stars.jpg";
					finalMediaFile = new File([blob], fileName, { type: blob.type });
					finalMediaType = "image";

					console.log("✅ Stars image loaded as file:", {
						name: fileName,
						size: finalMediaFile.size,
						type: finalMediaFile.type,
					});
				} catch (error) {
					console.error("❌ Error loading stars image:", error);
					// Continue with post submission without the image
				}
			} else if (
				selectedPostType === "pride" &&
				selectedProudOption?.type === "quest"
			) {
				try {
					// Use the quest complete image for quest completion posts
					const questCompleteImagePath = `${IMAGE_PATH}/newsfeed/questComplete.jpg`;
					console.log(
						"🏆 Fetching quest complete image:",
						questCompleteImagePath
					);

					// Fetch the quest complete image and convert to blob
					const response = await fetch(questCompleteImagePath);
					const blob = await response.blob();

					// Create a File object from the blob
					const fileName = "questComplete.jpg";
					finalMediaFile = new File([blob], fileName, { type: blob.type });
					finalMediaType = "image";

					console.log("✅ Quest complete image loaded as file:", {
						name: fileName,
						size: finalMediaFile.size,
						type: finalMediaFile.type,
					});
				} catch (error) {
					console.error("❌ Error loading quest complete image:", error);
					// Continue with post submission without the image
				}
			} else if (
				selectedPostType === "pride" &&
				selectedProudOption?.type === "secretSalutes"
			) {
				try {
					// Use the secret salutes image for secret salutes completion posts
					const secretSalutesImagePath = `${IMAGE_PATH}/newsfeed/secretSalutes.jpg`;
					console.log(
						"🎯 Fetching secret salutes image:",
						secretSalutesImagePath
					);

					// Fetch the secret salutes image and convert to blob
					const response = await fetch(secretSalutesImagePath);
					const blob = await response.blob();

					// Create a File object from the blob
					const fileName = "secretSalutes.jpg";
					finalMediaFile = new File([blob], fileName, { type: blob.type });
					finalMediaType = "image";

					console.log("✅ Secret salutes image loaded as file:", {
						name: fileName,
						size: finalMediaFile.size,
						type: finalMediaFile.type,
					});
				} catch (error) {
					console.error("❌ Error loading secret salutes image:", error);
					// Continue with post submission without the image
				}
			} else if (
				selectedPostType === "pride" &&
				selectedProudOption?.type === "firstGlow"
			) {
				try {
					// Use the first glow image for first glow post achievement
					const firstGlowImagePath = `${IMAGE_PATH}/newsfeed/firstglow.jpg`;
					console.log("✨ Fetching first glow image:", firstGlowImagePath);

					// Fetch the first glow image and convert to blob
					const response = await fetch(firstGlowImagePath);
					const blob = await response.blob();

					// Create a File object from the blob
					const fileName = "firstglow.jpg";
					finalMediaFile = new File([blob], fileName, { type: blob.type });
					finalMediaType = "image";

					console.log("✅ First glow image loaded as file:", {
						name: fileName,
						size: finalMediaFile.size,
						type: finalMediaFile.type,
					});
				} catch (error) {
					console.error("❌ Error loading first glow image:", error);
					// Continue with post submission without the image
				}
			}

			// For pride posts, determine Description and prideType based on selectedProudOption
			let prideDescription = content; // Default to user's content
			let prideType = null;
			let questIDForPride = 0;
			let creatureIDForPride = 0;

			if (selectedPostType === "pride" && selectedProudOption) {
				const optionType = selectedProudOption.type;

				if (optionType === "secretSalutes") {
					prideType = "SecretSalute";
					prideDescription = "SecretSalute";
				} else if (optionType === "star") {
					prideType = "StarSalute";
					prideDescription = "StarSalute";
				} else if (optionType === "firstGlow") {
					prideType = "FirstGlow";
					prideDescription = "FirstGlow";
				} else if (optionType === "quest") {
					prideType = "Quest";
					// Use QuestName if available, otherwise fallback
					prideDescription = selectedProudOption.QuestName || "Quest";
					questIDForPride = selectedProudOption.QuestID || 0;
				} else if (optionType === "creature") {
					prideType = "Creature";
					// Use CreatureName if available, otherwise fallback
					prideDescription = selectedProudOption.CreatureName || "Creature";
					// Map CreatureNumber to CreatureID (assuming they're the same or need mapping)
					creatureIDForPride = selectedProudOption.CreatureNumber || 0;
				}
			}

			const result = await newsfeed.handleSubmitPost(
				title,
				selectedPostType === "pride" && prideType ? prideDescription : content, // Use calculated Description for pride posts, otherwise use content
				finalMediaFile,
				finalMediaType,
				0, // recordingDuration — handled by Camera component
				contestid,
				selectedPostType === "pride" && questIDForPride > 0
					? questIDForPride
					: 0, // questID - use from selectedProudOption for quest pride posts
				0, // memberMilestoneID
				selectedPostType || "glow", // postType - defaults to "glow"
				true, // successToastShown
				prideType, // prideType - only set for pride posts
				creatureIDForPride // creatureID - only set for creature pride posts
			);
			console.log("✅ Post created:", result);

			// If post submission is successful
			if (result.success) {
				// Clear media after successful submission
				if (mediaFile) {
					handleRemoveMedia();
				}

				// Reset post content
				setContent("");
				setTitle("");
				setShowPostPreview(false);
				setSelectedProudOption(null);

				// Close the compose post modal/section
				if (onPostCreated) {
					onPostCreated();
				}
			}
		} catch (error) {
			console.error("Error submitting post:", error);
			setError("Failed to submit post. Please try again.");
		} finally {
			setIsPosting(false);
		}
	};

	// Helper function to get achievement title
	const getAchievementTitle = (achievement) => {
		// Determine the type of achievement and format accordingly
		switch (achievement.type) {
			case "quest":
				if (achievement.IsComplete) {
					return `Completed Quest: ${achievement.QuestName || "Quest"}`;
				}
				return achievement.QuestName || "Quest";
			case "star":
				if (achievement.IsComplete) {
					if (achievement.SalutesSent > achievement.Required) {
						return `Sent ${achievement.SalutesSent} Star Salutes - Above and Beyond!`;
					}
					return `Sent ${achievement.Required} Star Salutes`;
				}
				return `Star Salutes`;
			case "creature":
				return `Saved ${achievement.CreatureName || "A Creature"}`;
			case "secretSalutes":
				if (achievement.IsComplete) {
					return `Completed All Secret Salutes`;
				}
				return `Secret Salutes`;
			case "contestWin":
				return `Won ${achievement.ContestName}`;
			case "firstGlow":
				return `Created My First Glow Post!`;
			default:
				return "New Achievement Unlocked";
		}
	};

	// Helper function to get achievement body
	const getAchievementBody = (achievement) => {
		// Create detailed description based on achievement type
		switch (achievement.type) {
			case "quest":
				if (achievement.IsComplete) {
					return `I completed the ${achievement.QuestName || "quest"} today!`;
				}
				return `Complete all milestones to unlock this achievement`;
			case "star":
				if (achievement.IsComplete) {
					if (achievement.SalutesSent > achievement.Required) {
						const extra = achievement.SalutesSent - achievement.Required;
						return `I went above and beyond by sending ${achievement.SalutesSent} star salutes (${extra} more than required) to recognize others' amazing work!`;
					}
					return `I sent ${achievement.SalutesSent} star salute${achievement.SalutesSent !== 1 ? "s" : ""} to recognize others' amazing work!`;
				}
				return `Send ${achievement.Required} star salutes to unlock this achievement`;
			case "creature":
				return `I saved ${achievement.CreatureName || "a new beamkin"} and invited them to my santuary!`;
			case "secretSalutes":
				if (achievement.IsComplete) {
					return `I completed all my secret salute cards for the day and earned ${achievement.LumensEarned} lumens!`;
				}
				return `Complete all secret salute cards to unlock this achievement`;
			case "contestWin":
				return `I won the ${achievement.ContestName} contest today!`;
			case "firstGlow": {
				const glowTitle = achievement.Title
					? ` - "${achievement.Title.trim()}"`
					: "";
				return `I created my very first Glow post today${glowTitle}! I shared a good deed I did for someone else and earned ${achievement.LumensEarned || 0} lumens for spreading positivity!`;
			}
			default:
				return "I unlocked a new achievement!";
		}
	};

	// Format duration helper function
	const formatDuration = (seconds) => {
		// Handle invalid values
		if (!seconds || !isFinite(seconds) || isNaN(seconds) || seconds < 0) {
			return "00:00";
		}
		const mins = Math.floor(seconds / 60);
		const secs = Math.floor(seconds % 60);
		return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
	};

	// Calculate time remaining for an event
	const getTimeRemaining = (endTime) => {
		if (!endTime) {
			return null;
		}

		const now = new Date();
		const end = new Date(endTime);
		const diff = end - now;

		if (diff <= 0) {
			return "Ended";
		}

		const days = Math.floor(diff / (1000 * 60 * 60 * 24));
		const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
		const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

		if (days > 0) {
			return `${days}d ${hours}h left`;
		} else if (hours > 0) {
			return `${hours}h ${minutes}m left`;
		}
		return `${minutes}m left`;
	};

	// Get event type icon component
	const getEventTypeIcon = (eventType) => {
		const iconClass = "w-5 h-5 text-white";
		switch (eventType?.toLowerCase()) {
			case "challenge":
				return <Flag className={iconClass} />;
			case "mission":
				return <span className="text-2xl">🚀</span>;
			case "quest":
				return <span className="text-2xl">⚔️</span>;
			case "competition":
				return <span className="text-2xl">🏆</span>;
			case "event":
				return <span className="text-2xl">📅</span>;
			default:
				return <span className="text-2xl">⭐</span>;
		}
	};

	if (userStore.getAccessLevel() < 2) {
		return (
			<Card className="mb-6 max-w-full overflow-hidden border border-border bg-card shadow-sm dark:border-border dark:bg-card">
				<CardHeader className="relative shrink-0 pb-2">
					<button
						onClick={onClose}
						className="absolute right-0 top-0 rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
						aria-label="Close"
					>
						<X className="h-5 w-5" />
					</button>
				</CardHeader>
				<CardContent className="min-w-0 shrink-0 overflow-hidden px-4 pb-6 pt-0 sm:px-6">
					<h3 className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-100">
						Welcome to the newsfeed!
					</h3>
					<p className="mb-5 max-w-full break-words text-sm leading-relaxed text-slate-600 dark:text-slate-300">
						Here, members post about positive impact they are making in the real
						world. Every post you see has been verified by our party. Members
						earn points for every verified post. Create an account to share your
						own achievements or interact with existing posts!
					</p>
					<Button
						onClick={() =>
							navigate("/story-landing?openModal=receive-transmission")
						}
						className="w-full bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600"
					>
						Join the Mission
					</Button>
				</CardContent>
			</Card>
		);
	}
	return (
		<>
			<Card
				className={`relative mb-4 min-w-0 overflow-hidden rounded-2xl border-2 bg-white shadow-lg dark:bg-gray-800 sm:mb-6 ${borderColor}`}
			>
				{contests.length > 0 ? (
					<ContestField
						contests={contests}
						contestIndex={contestIndex}
						onContestChange={handleContestChange}
						onClose={onClose}
					/>
				) : null}

				{/* Active Mission Banner - Hide for "Glow Game" */}
				{contests.length > 0 &&
				contests[contestIndex].toLowerCase() !== "glow game" &&
				activeEvent ? (
					<div className="mx-3 mb-0 mt-3 min-w-0 rounded-xl border-2 border-amber-300 bg-gradient-to-r from-amber-100 to-yellow-100 p-3 shadow-md dark:border-amber-700 dark:from-amber-900/30 dark:to-yellow-900/30 sm:mx-6 sm:mt-4 sm:p-4">
						<div className="flex items-start gap-3">
							<div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 shadow-md">
								{getEventTypeIcon(activeEvent.EventType)}
							</div>
							<div className="flex-1">
								<div className="mb-1 flex items-center justify-between gap-2">
									<div className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
										{activeEvent.EventType || "Event"} • Active Now
									</div>
									{activeEvent.EndTime ? (
										<div className="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-bold text-amber-800 dark:bg-amber-900/50 dark:text-amber-300">
											⏱️ {getTimeRemaining(activeEvent.EndTime)}
										</div>
									) : null}
								</div>
								<div className="text-sm font-bold text-gray-900 dark:text-white">
									{activeEvent.EventTitle ||
										activeEvent.EventName ||
										"Current Event"}
								</div>
								{activeEvent.EventDescription ? (
									<div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
										{activeEvent.EventDescription}
									</div>
								) : null}
							</div>
						</div>
					</div>
				) : null}

				{/* Preview mode banner for pride posts */}
				{showPostPreview ? (
					<div className="mx-3 mb-0 mt-3 min-w-0 rounded-xl border-2 border-purple-500 bg-purple-100 p-3 shadow-md dark:bg-purple-900/30 sm:mx-6 sm:mt-4 sm:p-4">
						<div className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
							<Award className="h-5 w-5" />
							<span className="font-semibold">Preview Mode</span>
						</div>
						<div className="mt-1 text-sm text-purple-600 dark:text-purple-400">
							Review your post below. Click "Post" to share or "Cancel" to go
							back and choose a different achievement.
						</div>
					</div>
				) : null}

				<CardContent className="min-w-0 px-2 pb-2 pt-2 sm:px-3 sm:pb-3 sm:pt-3">
					{/* Post Type Selector - Show with descriptions when no post type selected and no media */}
					{!selectedPostType && !mediaPreview ? (
						<div className="py-4 sm:py-6">
							<PostTypeSelector
								selectedPostType={selectedPostType}
								onPostTypeSelect={onPostTypeSelect}
								mediaFile={mediaFile}
								mediaPreview={mediaPreview}
								onShowCaptureSelector={() => setShowCaptureSelector(true)}
								isGlowGame={
									contests[contestIndex]?.toLowerCase() === "glow game" ||
									contests[contestIndex]?.toLowerCase() === "the glow game"
								}
								userLumens={userStore.user?.CurrentLumens || 0}
							/>
						</div>
					) : null}
					{!mediaPreview && selectedPostType && selectedPostType !== "pride" ? (
						// Show post type selector when post type is selected but no media (except for pride which shows achievement list)
						<div className="py-4 sm:py-6">
							{contests[contestIndex]?.toLowerCase() === "glow game" ||
							contests[contestIndex]?.toLowerCase() === "the glow game" ? (
								// Show all three post types for Glow Game in vertical list
								<div className="space-y-3">
									{/* Glow Button */}
									<button
										onClick={() => {
											if (
												selectedPostType === "glow" &&
												!mediaFile &&
												!mediaPreview
											) {
												setShowCaptureSelector(true);
											} else {
												onPostTypeSelect && onPostTypeSelect("glow");
											}
										}}
										className={`flex w-full transform items-center gap-4 rounded-xl bg-gradient-to-r from-yellow-400 to-yellow-500 p-4 shadow-lg transition-all hover:scale-105 hover:from-yellow-500 hover:to-yellow-600 ${
											selectedPostType === "glow"
												? "ring-4 ring-yellow-300"
												: ""
										}`}
									>
										<div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white">
											<Star size={24} className="text-yellow-500" />
										</div>
										<div className="flex-1 text-left">
											<h3 className="text-lg font-bold text-white">
												Create Glow Post
											</h3>
											<p className="text-sm text-yellow-100">
												Share a kind act you did for others
											</p>
										</div>
										<div className="flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-white/20 px-3 py-2 backdrop-blur-sm">
											<div className="relative h-4 w-4">
												<div className="absolute inset-0 animate-pulse rounded-full bg-yellow-200 blur-sm" />
												<div className="relative h-4 w-4 rounded-full bg-gradient-to-br from-yellow-100 to-yellow-200" />
											</div>
											<span className="text-sm font-bold text-white">1-15</span>
										</div>
									</button>

									{/* Proud Button */}
									<button
										onClick={() => {
											onPostTypeSelect && onPostTypeSelect("pride");
										}}
										className={`flex w-full transform items-center gap-4 rounded-xl bg-gradient-to-r from-purple-500 to-purple-600 p-4 shadow-lg transition-all hover:scale-105 hover:from-purple-600 hover:to-purple-700 ${
											selectedPostType === "pride"
												? "ring-4 ring-purple-300"
												: ""
										}`}
									>
										<div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white">
											<Award size={24} className="text-purple-500" />
										</div>
										<div className="flex-1 text-left">
											<h3 className="text-lg font-bold text-white">
												Create Proud Post
											</h3>
											<p className="text-sm text-purple-100">
												Celebrate your achievements
											</p>
										</div>
									</button>

									{/* Grateful Button */}
									<button
										onClick={() => {
											const currentLumens = userStore.user?.CurrentLumens || 0;
											if (currentLumens < 2) {
												return;
											} // Don't allow if insufficient lumens
											if (
												selectedPostType === "grateful" &&
												!mediaFile &&
												!mediaPreview
											) {
												setShowCaptureSelector(true);
											} else {
												onPostTypeSelect && onPostTypeSelect("grateful");
											}
										}}
										disabled={(userStore.user?.CurrentLumens || 0) < 2}
										className={`flex w-full transform items-center gap-4 rounded-xl p-4 shadow-lg transition-all ${
											(userStore.user?.CurrentLumens || 0) >= 2
												? `bg-gradient-to-r from-green-500 to-green-600 hover:scale-105 hover:from-green-600 hover:to-green-700 ${
														selectedPostType === "grateful"
															? "ring-4 ring-green-300"
															: ""
													}`
												: "cursor-not-allowed bg-gradient-to-r from-gray-400 to-gray-500 opacity-70"
										}`}
									>
										<div
											className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white ${(userStore.user?.CurrentLumens || 0) < 2 ? "opacity-50" : ""}`}
										>
											<Heart
												size={24}
												className={
													(userStore.user?.CurrentLumens || 0) >= 2
														? "text-green-500"
														: "text-gray-400"
												}
											/>
										</div>
										<div className="flex-1 text-left">
											<h3 className="text-lg font-bold text-white">
												Create Grateful Post
											</h3>
											{(userStore.user?.CurrentLumens || 0) >= 2 ? (
												<p className="text-sm text-green-100">
													Express gratitude to someone special
												</p>
											) : (
												<p className="text-sm font-semibold text-red-200">
													Insufficient Lumens
												</p>
											)}
										</div>
										<div className="flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-red-500/30 px-3 py-2 backdrop-blur-sm">
											<div className="relative h-4 w-4">
												<div
													className={`absolute inset-0 rounded-full bg-red-200 blur-sm ${(userStore.user?.CurrentLumens || 0) >= 2 ? "animate-pulse" : ""}`}
												/>
												<div className="relative h-4 w-4 rounded-full bg-gradient-to-br from-red-100 to-red-200" />
											</div>
											<span className="text-sm font-bold text-white">-2</span>
										</div>
									</button>
								</div>
							) : (
								// Show Submit for Contest button for other contests
								<button
									onClick={() => {
										if (!mediaFile && !mediaPreview) {
											setShowCaptureSelector(true);
										}
									}}
									className="flex w-full transform items-center gap-4 rounded-xl bg-gradient-to-r from-yellow-400 to-yellow-500 p-4 shadow-lg transition-all hover:scale-105 hover:from-yellow-500 hover:to-yellow-600"
								>
									<div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white">
										<Star size={24} className="text-yellow-500" />
									</div>
									<div className="flex-1 text-left">
										<h3 className="text-lg font-bold text-white">
											Submit for Contest
										</h3>
										<p className="text-sm text-yellow-100">
											Your submission will also be graded for glow
										</p>
									</div>
									<div className="flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-white/20 px-3 py-2 backdrop-blur-sm">
										<div className="relative h-4 w-4">
											<div className="absolute inset-0 animate-pulse rounded-full bg-yellow-200 blur-sm" />
											<div className="relative h-4 w-4 rounded-full bg-gradient-to-br from-yellow-100 to-yellow-200" />
										</div>
										<span className="text-sm font-bold text-white">1-15</span>
									</div>
								</button>
							)}
						</div>
					) : null}

					{/* Post composer - Full width */}
					<div className="w-full min-w-0">
						{/* Show achievement list for proud posts */}
						{selectedPostType === "pride" && !showPostPreview ? (
							<div className="mb-3">
								{/* Back button to return to post type selection */}
								<Button
									onClick={() => {
										onPostTypeSelect && onPostTypeSelect(null);
										setSelectedProudOption(null);
									}}
									variant="outline"
									size="sm"
									className="mb-3 border-purple-500 bg-white text-purple-600 hover:bg-purple-50 dark:border-purple-400 dark:bg-gray-800 dark:text-purple-400 dark:hover:bg-purple-900/20"
								>
									<ArrowLeft size={16} className="mr-1" />
									Back to Post Types
								</Button>
								<div className="mb-3 text-center">
									<h3 className="text-sm font-bold text-purple-700 dark:text-purple-400">
										Choose an Achievement to Share
									</h3>
								</div>

								{/* Container with darker background */}
								<div className="rounded-xl bg-gray-100 p-3 dark:bg-gray-900/60">
									{isLoadingProudOptions ? (
										<div className="flex items-center justify-center py-6">
											<div className="text-sm text-muted-foreground dark:text-muted-foreground">
												Loading achievements...
											</div>
										</div>
									) : !Array.isArray(proudOptions) ||
									  proudOptions.length === 0 ? (
										<div className="rounded-lg bg-white p-3 text-center dark:bg-gray-800">
											<div className="text-xs text-muted-foreground dark:text-muted-foreground">
												No achievements available to share at this time.
												Complete quests and milestones to unlock achievements!
											</div>
										</div>
									) : (
										<div className="scrollbar-hidden max-h-80 space-y-3 overflow-y-auto">
											{Array.isArray(proudOptions) &&
												[...proudOptions]
													.sort((a, b) => {
														// Sort completed (IsComplete = true) to the top
														if (a.IsComplete && !b.IsComplete) {
															return -1;
														}
														if (!a.IsComplete && b.IsComplete) {
															return 1;
														}
														return 0;
													})
													.map((option, index) => {
														const isDisabled = !option.IsComplete;
														const canPost = option.CanPost !== false; // Default to true if not set
														const alreadyPosted = option.AlreadyPosted === true;
														const isPostable =
															!isDisabled && canPost && !alreadyPosted;

														return (
															<div
																key={index}
																className={`w-full rounded-xl border p-4 text-left shadow-sm transition-all ${
																	isDisabled
																		? "cursor-not-allowed border-l-4 border-blue-400 border-y-blue-200 border-r-blue-200 bg-blue-50 dark:border-y-blue-600 dark:border-r-blue-600 dark:bg-blue-900/20"
																		: alreadyPosted
																			? "border-l-4 border-gray-400 border-y-gray-300 border-r-gray-300 bg-gray-100 dark:border-y-gray-600 dark:border-r-gray-600 dark:bg-gray-800/60"
																			: "border-l-4 border-purple-500 border-y-purple-400 border-r-purple-400 bg-purple-500 dark:border-purple-400 dark:bg-purple-500"
																}`}
															>
																<div className="w-full">
																	<div className="flex items-center justify-between">
																		<div
																			className={`text-base font-bold ${isDisabled ? "text-blue-700 dark:text-blue-300" : alreadyPosted ? "text-gray-700 dark:text-gray-300" : "text-white"}`}
																		>
																			{getAchievementTitle(option)}
																		</div>
																		{alreadyPosted ? (
																			<span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-300">
																				Already Posted Today
																			</span>
																		) : null}
																	</div>
																	<div
																		className={`mt-2 text-sm ${isDisabled ? "text-blue-600 dark:text-blue-400" : alreadyPosted ? "text-gray-600 dark:text-gray-400" : "text-purple-100"}`}
																	>
																		{getAchievementBody(option)}
																	</div>

																	{/* Post button for completed achievements that can be posted */}
																	{isPostable ? (
																		<Button
																			onClick={(e) => {
																				e.stopPropagation();
																				setSelectedProudOption(option);
																				const achievementBody =
																					getAchievementBody(option);
																				setTitle("");
																				setContent(achievementBody);
																				setShowPostPreview(true);
																			}}
																			size="sm"
																			className="mt-3 w-full bg-white font-semibold text-purple-600 shadow-md hover:bg-purple-50"
																		>
																			Post This Achievement
																		</Button>
																	) : null}

																	{/* Message for already posted achievements */}
																	{alreadyPosted && option.IsComplete ? (
																		<div className="mt-3 rounded-md bg-gray-200 px-3 py-2 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-300">
																			You have already posted this achievement
																			today.
																		</div>
																	) : null}

																	{/* Progress bars for incomplete achievements */}
																	{isDisabled ? (
																		<div className="mt-2">
																			{option.type === "secretSalutes" && (
																				<>
																					<div className="mb-1 flex justify-between text-xs">
																						<span className="font-medium text-blue-600 dark:text-blue-400">
																							Progress
																						</span>
																						<span className="font-medium text-blue-600 dark:text-blue-400">
																							{option.Completed}/{option.Total}
																						</span>
																					</div>
																					<div className="mb-2 h-2 w-full rounded-full bg-blue-200 dark:bg-blue-800/50">
																						<div
																							className="h-2 rounded-full bg-purple-500 transition-all"
																							style={{
																								width: `${option.Total > 0 ? (option.Completed / option.Total) * 100 : 0}%`,
																							}}
																						/>
																					</div>
																					<button
																						onClick={(e) => {
																							e.stopPropagation();
																							navigate("/secret-salute");
																						}}
																						className="mt-2 w-full rounded-md bg-purple-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-purple-600"
																					>
																						Go to Secret Salutes
																					</button>
																				</>
																			)}
																			{option.type === "star" && (
																				<>
																					<div className="mb-1 flex justify-between text-xs">
																						<span className="font-medium text-blue-600 dark:text-blue-400">
																							Progress
																						</span>
																						<span className="font-medium text-blue-600 dark:text-blue-400">
																							{option.SalutesSent}/
																							{option.Required}
																						</span>
																					</div>
																					<div className="mb-2 h-2 w-full rounded-full bg-blue-200 dark:bg-blue-800/50">
																						<div
																							className="h-2 rounded-full bg-yellow-500 transition-all"
																							style={{
																								width: `${option.Required > 0 ? (option.SalutesSent / option.Required) * 100 : 0}%`,
																							}}
																						/>
																					</div>
																					<button
																						onClick={(e) => {
																							e.stopPropagation();
																							navigate("/starsalute");
																						}}
																						className="mt-2 w-full rounded-md bg-yellow-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-yellow-600"
																					>
																						Go to Star Salutes
																					</button>
																				</>
																			)}
																			{option.type === "quest" && (
																				<>
																					<div className="mb-1 flex justify-between text-xs">
																						<span className="font-medium text-blue-600 dark:text-blue-400">
																							Milestones
																						</span>
																						<span className="font-medium text-blue-600 dark:text-blue-400">
																							{option.MilestonesCompleted}/
																							{option.TotalMilestones}
																						</span>
																					</div>
																					<div className="mb-2 h-2 w-full rounded-full bg-blue-200 dark:bg-blue-800/50">
																						<div
																							className="h-2 rounded-full bg-green-500 transition-all"
																							style={{
																								width: `${option.TotalMilestones > 0 ? (option.MilestonesCompleted / option.TotalMilestones) * 100 : 0}%`,
																							}}
																						/>
																					</div>
																					<button
																						onClick={(e) => {
																							e.stopPropagation();
																							navigate(
																								"/choose-quest?questID=3&simple=1"
																							);
																						}}
																						className="mt-2 w-full rounded-md bg-green-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-green-600"
																					>
																						Go to Quests
																					</button>
																				</>
																			)}
																		</div>
																	) : null}
																</div>
															</div>
														);
													})}
										</div>
									)}
								</div>
							</div>
						) : selectedPostType !== "pride" || showPostPreview ? (
							<>
								{/* Insufficient lumens message for grateful posts */}
								{selectedPostType === "grateful" &&
									(userStore.user?.CurrentLumens || 0) < 2 && (
										<div className="mb-3 rounded-lg border-2 border-red-300 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/20">
											<div className="mb-2 flex items-center gap-2 text-red-700 dark:text-red-400">
												<Star className="h-5 w-5" />
												<span className="text-sm font-semibold">
													You need at least 2 lumens to post a grateful post.
												</span>
											</div>
											<p className="text-xs text-red-600 dark:text-red-500">
												Complete quests and activities to earn more lumens!
											</p>
										</div>
									)}
							</>
						) : null}

						{/* Creature image preview for pride posts */}
						{showPostPreview && selectedProudOption?.type === "creature" ? (
							<>
								<div className="relative aspect-square w-full overflow-hidden rounded-lg bg-black">
									<img
										src={(() => {
											const creatureMap = {
												1: `${IMAGE_PATH}/quest/creatures/Jokeymon.png`,
												2: `${IMAGE_PATH}/quest/creatures/Patience.png`,
												3: `${IMAGE_PATH}/quest/creatures/Stevie.png`,
												4: `${IMAGE_PATH}/quest/creatures/bluekin.png`,
												5: `${IMAGE_PATH}/quest/creatures/lightkin.png`,
											};
											return creatureMap[selectedProudOption.CreatureNumber];
										})()}
										alt={selectedProudOption.CreatureName || "Creature"}
										className="h-full w-full object-cover"
									/>
									{/* Post Type Badge - Top Left */}
									<div className="absolute -right-2 top-2 z-10 rounded-full bg-purple-500 px-4 py-2 text-sm font-bold text-white">
										<div className="flex items-center gap-2">
											<Award size={16} />
											<span>Proud Post</span>
										</div>
									</div>
								</div>
								{/* Caption text below image */}
								{content ? (
									<div className="mt-3 px-2">
										<p className="text-sm leading-relaxed text-foreground">
											{content}
										</p>
									</div>
								) : null}
							</>
						) : null}

						{/* Stars image preview for star salute pride posts */}
						{showPostPreview && selectedProudOption?.type === "star" ? (
							<>
								<div className="relative aspect-square w-full overflow-hidden rounded-lg bg-black">
									<img
										src={`${IMAGE_PATH}/newsfeed/stars.jpg`}
										alt="Star Salute"
										className="h-full w-full object-cover"
									/>
									{/* Post Type Badge - Top Left */}
									<div className="absolute left-2 top-2 z-10 rounded-full bg-purple-500 px-4 py-2 text-sm font-bold text-white">
										<div className="flex items-center gap-2">
											<Award size={16} />
											<span>Proud Post</span>
										</div>
									</div>
								</div>
								{/* Caption text below image */}
								{content ? (
									<div className="mt-3 px-2">
										<p className="text-sm leading-relaxed text-foreground">
											{content}
										</p>
									</div>
								) : null}
							</>
						) : null}

						{/* Quest complete image preview for quest completion pride posts */}
						{showPostPreview && selectedProudOption?.type === "quest" ? (
							<>
								<div className="relative aspect-square w-full overflow-hidden rounded-lg bg-black">
									<img
										src={`${IMAGE_PATH}/newsfeed/questComplete.jpg`}
										alt="Quest Complete"
										className="h-full w-full object-cover"
									/>
									{/* Post Type Badge - Top Left */}
									<div className="absolute left-2 top-2 z-10 rounded-full bg-purple-500 px-4 py-2 text-sm font-bold text-white">
										<div className="flex items-center gap-2">
											<Award size={16} />
											<span>Proud Post</span>
										</div>
									</div>
								</div>
								{/* Caption text below image */}
								{content ? (
									<div className="mt-3 px-2">
										<p className="text-sm leading-relaxed text-foreground">
											{content}
										</p>
									</div>
								) : null}
							</>
						) : null}

						{/* Secret salutes image preview for secret salutes completion pride posts */}
						{showPostPreview &&
						selectedProudOption?.type === "secretSalutes" ? (
							<>
								<div className="relative aspect-square w-full overflow-hidden rounded-lg bg-black">
									<img
										src={`${IMAGE_PATH}/newsfeed/secretSalutes.jpg`}
										alt="Secret Salutes Complete"
										className="h-full w-full object-cover"
									/>
									{/* Post Type Badge - Top Left */}
									<div className="absolute left-2 top-2 z-10 rounded-full bg-purple-500 px-4 py-2 text-sm font-bold text-white">
										<div className="flex items-center gap-2">
											<Award size={16} />
											<span>Proud Post</span>
										</div>
									</div>
								</div>
								{/* Caption text below image */}
								{content ? (
									<div className="mt-3 px-2">
										<p className="text-sm leading-relaxed text-foreground">
											{content}
										</p>
									</div>
								) : null}
							</>
						) : null}

						{/* First Glow image preview for first glow post pride achievement */}
						{showPostPreview && selectedProudOption?.type === "firstGlow" ? (
							<>
								<div className="relative aspect-square w-full overflow-hidden rounded-lg bg-black">
									<img
										src={`${IMAGE_PATH}/newsfeed/firstglow.jpg`}
										alt="First Glow Post"
										className="h-full w-full object-cover"
									/>
									{/* Post Type Badge - Top Left */}
									<div className="absolute left-2 top-2 z-10 rounded-full bg-purple-500 px-4 py-2 text-sm font-bold text-white">
										<div className="flex items-center gap-2">
											<Award size={16} />
											<span>Proud Post</span>
										</div>
									</div>
								</div>
								{/* Caption text below image */}
								{content ? (
									<div className="mt-3 px-2">
										<p className="text-sm leading-relaxed text-foreground">
											{content}
										</p>
									</div>
								) : null}
							</>
						) : null}

						{/* Error display */}
						{error ? (
							<div className="mb-2 text-sm text-red-500 dark:text-red-400">
								{error}
							</div>
						) : null}

						{/* Media Preview - Use MediaCaptureSection component */}
						<MediaCaptureSection
							mediaPreview={mediaPreview}
							mediaType={mediaType}
							mediaFile={mediaFile}
							videoRef={videoRef}
							recordingDuration={0}
							formatDuration={formatDuration}
							onRemoveMedia={handleRemoveMedia}
							selectedPostType={selectedPostType}
							showPostPreview={showPostPreview}
							content={content}
							onContentChange={setContent}
							onContentBlur={handleContentBlur}
							textareaRef={textareaRef}
							isListening={isListening}
							onStartSpeechRecognition={startSpeechRecognition}
							onStopSpeechRecognition={stopSpeechRecognition}
							contestName={contests[contestIndex]}
							userName={userStore.user?.Username}
							isPosting={isPosting}
							isRecording={false}
							currentLumens={userStore.user?.CurrentLumens}
						/>

						{/* Hidden file inputs */}
						<input
							type="file"
							ref={fileInputRef}
							onChange={(e) => handleFileSelect(e, "image")}
							accept="image/*"
							className="hidden"
							disabled={isPosting}
						/>
						<input
							type="file"
							ref={videoInputRef}
							onChange={(e) => handleFileSelect(e, "video")}
							accept="video/*"
							className="hidden"
							disabled={isPosting}
						/>
						<input
							type="file"
							ref={audioInputRef}
							onChange={(e) => handleFileSelect(e, "audio")}
							accept="audio/*"
							className="hidden"
							disabled={isPosting}
						/>

						{/* Add Media Section - Hide for proud posts, grateful posts without lumens, AND when media is already uploaded */}

						{/* Post button */}
						<div ref={postButtonRef} className="mt-3">
							<div className="flex justify-end">
								{showPostPreview ? (
									<div className="flex gap-2">
										<Button
											onClick={() => {
												setShowPostPreview(false);
												setTitle("");
												setContent("");
											}}
											disabled={isPosting}
											size="sm"
											variant="outline"
											className="flex items-center"
										>
											<X size={16} className="mr-2" />
											Cancel
										</Button>
										{!(
											selectedPostType === "grateful" &&
											(userStore.user?.CurrentLumens || 0) < 2
										) &&
										(mediaType === "audio" || (content && content.trim())) ? (
											<Button
												onClick={handleSubmit}
												disabled={isPosting}
												size="sm"
												className="flex items-center border-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md hover:from-purple-700 hover:to-pink-700"
											>
												<Send size={16} className="mr-2" />
												{isPosting ? "Posting..." : "Post"}
											</Button>
										) : null}
									</div>
								) : selectedPostType !== "pride" ? (
									!(
										selectedPostType === "grateful" &&
										(userStore.user?.CurrentLumens || 0) < 2
									) &&
									(mediaType === "audio"
										? !!(mediaFile || mediaPreview)
										: !!(content && content.trim())) && (
										<Button
											onClick={handleSubmit}
											disabled={
												(mediaType !== "audio" &&
													(!content || !(mediaFile || mediaPreview))) ||
												(mediaType === "audio" &&
													!(mediaFile || mediaPreview)) ||
												isPosting
											}
											size="sm"
											className="flex items-center border-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md hover:from-purple-700 hover:to-pink-700"
										>
											<Send size={16} className="mr-2" />
											{isPosting ? "Posting..." : "Post"}
										</Button>
									)
								) : null}
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Capture Method Selector */}
			{showCaptureSelector ? (
				<CaptureMethodSelector
					onSelect={handleCaptureMethodSelect}
					onClose={() => setShowCaptureSelector(false)}
					postType={selectedPostType}
					isContest={
						contests[contestIndex]?.toLowerCase() !== "glow game" &&
						contests[contestIndex]?.toLowerCase() !== "the glow game"
					}
				/>
			) : null}

			{/* Custom Camera Component */}
			{showCamera ? (
				<Camera
					onCapture={handleCameraCapture}
					onClose={(reason) => {
						setShowCamera(false);
						setAutoTriggerUpload(false);
						if (reason !== "completed") {
							setShowCaptureSelector(true);
						}
					}}
					mode={cameraMode}
					showPreview
					autoTriggerUpload={autoTriggerUpload}
					postType={selectedPostType}
					isContest={
						contests[contestIndex]?.toLowerCase() !== "glow game" &&
						contests[contestIndex]?.toLowerCase() !== "the glow game"
					}
					delayInitialization={false}
				/>
			) : null}
		</>
	);
};

export default ComposePost;
